import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { CalendarClock, Paperclip, Send, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { mailbox, mailErrorMessage, MAX_ATTACHMENT_BYTES } from "../../api/mailbox";
import type { SendInput } from "../../api/mailbox";
import type { MailDetail } from "../../api/types";
import { formatSize } from "./MailReader";
import type { ReplyKind } from "./MailReader";
import { useDraftAutosave } from "./useDraftAutosave";
import { scheduleTimeError, taipeiInputToIso } from "./ScheduledList";

// 寫信 / 回覆 / 全部回覆 / 轉寄(規格 §2.4),用**本人的信箱**寄出。
//
// - 簽名檔(§2.6)由前端帶進內文:空一行之後;回信 / 轉寄時簽名在引用的原信**上面**。
//   後端不自動加簽名 —— 帶進來之後本人看得到、刪得掉,寄出去的就是畫面上這一份。
//   簽名還沒讀回來時先開欄位;讀回來時本人還沒動過內文才補上(不覆蓋已經打的字)。
// - 附件總計 15 MB,前端先擋(後端也擋,回 413)。
// - 寄出失敗不關掉、不清空:錯誤交給頁面判斷要不要切成唯讀(雲端連不上 / 密碼被拒)。
// - 草稿自動儲存(§2.6,`useDraftAutosave`):動過、停手 10 秒存到雲端草稿匣。寄出 / 排程時把草稿編號
//   交給 `/send`(後端寄完刪掉);**放棄**(頁面確認過之後呼叫 `discard()`)刪掉存過的草稿。
// - 排程寄送(§2.6):「排程…」選時間 →「排程寄出」,送的是同一支 `/send` 多帶 `send_at`。
// - 關閉 / 取消一律交給頁面(`onClose`):動過要先問「要放棄正在寫的信嗎?」,問過才 `discard()`。

export type ComposeMode = { kind: "new" } | { kind: ReplyKind; original: MailDetail };

export interface MailComposerProps {
  mode: ComposeMode;
  /** 自己的信箱。「全部回覆」要把自己拿掉。 */
  selfEmail: string;
  /** 雲端連不上 / 密碼被拒:寄不出去,「寄出」停用。 */
  disabled: boolean;
  /** 按了關閉 / 取消。頁面決定要不要先問(動過才問)。 */
  onClose: () => void;
  /** 寄出了。`refused` 是郵件主機不收的收件人(頁面要留一則關掉才消失的警告)。 */
  onSent: (refused: string[]) => void;
  /** 排好了(系統保管,到時候寄)。 */
  onScheduled: () => void;
  /**
   * 本人有沒有動過這封信(收件人、主旨、內文裡預填的簽名 / 引用以外、附件)。
   * 頁面在寫到一半又去點別封信時,靠它決定要不要先問。
   */
  onDirtyChange?: (dirty: boolean) => void;
  /** 寄出失敗。頁面依錯誤碼切換狀態列(唯讀 / 重新輸入密碼)。 */
  onError: (err: unknown) => void;
}

/** HTML 信沒有純文字版本時,引用要用的文字。DOMParser 不執行任何程式。 */
function plainText(detail: MailDetail): string {
  if (detail.text) return detail.text;
  if (!detail.html) return "";
  try {
    return new DOMParser().parseFromString(detail.html, "text/html").body.textContent ?? "";
  } catch {
    return "";
  }
}

function prefixed(subject: string, prefix: "Re" | "Fwd"): string {
  const clean = subject.trim();
  const already = prefix === "Re" ? /^re\s*:/i : /^(fwd?|fw)\s*:/i;
  return already.test(clean) ? clean : `${prefix}: ${clean}`;
}

function sender(detail: MailDetail): string {
  return detail.from_name ? `${detail.from_name} <${detail.from_addr}>` : detail.from_addr;
}

/** 引用 / 轉寄的那一段(沒有就是空字串)。 */
function quoteBlock(mode: ComposeMode): string {
  if (mode.kind === "new") return "";
  const original = mode.original;
  const text = plainText(original).replace(/\r\n/g, "\n").replace(/\s+$/, "");
  if (mode.kind === "forward") {
    return [
      "---------- 轉寄的信 ----------",
      `寄件人:${sender(original)}`,
      `日期:${formatTaipeiDateTime(original.date)}`,
      `主旨:${original.subject}`,
      `收件人:${original.to_addrs.join(", ")}`,
      "",
      text,
    ].join("\n");
  }
  const quoted = text
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
  return `${formatTaipeiDateTime(original.date)},${sender(original)} 寫道:\n${quoted}`;
}

/** 內文:空一行(游標停在這裡)→ 簽名 → 引用的原信。 */
export function composeBody(signature: string, quote: string): string {
  const sig = signature.replace(/\s+$/, "");
  if (!sig && !quote) return "";
  return "\n\n" + [sig, quote].filter(Boolean).join("\n\n");
}

/** 寄件備份(自己寄出的信):回覆的對象是原信的收件人,不是自己。 */
const SENT_FOLDER = "INBOX.Sent";

/** 去掉空白、自己、重複與 `exclude` 裡的(都不分大小寫)。 */
function people(addrs: string[], self: string, exclude: Set<string> = new Set()): string[] {
  const seen = new Set<string>();
  return addrs.filter((addr) => {
    const key = addr.trim().toLowerCase();
    if (!key || key === self || exclude.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * 預填的收件人 / 副本 / 主旨。
 *
 * - 回覆:收件人 = 原寄件人;寄件備份裡的信(寄件人是自己)改成原信的收件人。
 * - 全部回覆:收件人同上;原信的其他收件人(索引裡 To 與 Cc 合在一起)去掉自己、去掉已經在
 *   收件人裡的,放副本。
 */
export function initialFields(mode: ComposeMode, selfEmail: string) {
  if (mode.kind === "new") return { to: "", cc: "", subject: "" };
  const original = mode.original;
  if (mode.kind === "forward") return { to: "", cc: "", subject: prefixed(original.subject, "Fwd") };
  const self = selfEmail.trim().toLowerCase();
  const fromSent = original.folder === SENT_FOLDER;
  const to = people(fromSent ? original.to_addrs : [original.from_addr], self);
  const cc =
    mode.kind === "replyAll" && !fromSent
      ? people(original.to_addrs, self, new Set(to.map((addr) => addr.trim().toLowerCase())))
      : [];
  return { to: to.join(", "), cc: cc.join(", "), subject: prefixed(original.subject, "Re") };
}

/** 頁面拿來「放棄這封信」的把手(確認過之後才呼叫)。 */
export interface MailComposerHandle {
  /** 停止自動儲存、刪掉存過的草稿。 */
  discard: () => Promise<void>;
  /** 雲端上有沒有這封的草稿(存過)。 */
  hasDraft: () => boolean;
}

export const MailComposer = forwardRef<MailComposerHandle, MailComposerProps>(function MailComposer(
  { mode, selfEmail, disabled, onClose, onSent, onScheduled, onError, onDirtyChange },
  ref,
) {
  const toast = useToast();
  const quote = useRef(quoteBlock(mode));
  const initial = useRef(initialFields(mode, selfEmail));
  const [to, setTo] = useState(initial.current.to);
  const [cc, setCc] = useState(initial.current.cc);
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(initial.current.subject);
  const [body, setBody] = useState(() => composeBody("", quote.current));
  // 最近一次由程式預填的內文(引用、簽名)。內文與它不同 = 本人動過
  const autoBody = useRef(composeBody("", quote.current));
  const touched = useRef(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const bodyId = useId();
  const filesId = useId();

  // 簽名檔:讀回來時本人還沒動過內文才帶進去
  useEffect(() => {
    let alive = true;
    mailbox
      .signature()
      .then((got) => {
        const sig = typeof got?.signature === "string" ? got.signature : "";
        if (alive && sig && !touched.current) {
          autoBody.current = composeBody(sig, quote.current);
          setBody(autoBody.current);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const total = files.reduce((sum, file) => sum + file.size, 0);

  const dirty =
    to !== initial.current.to ||
    cc !== initial.current.cc ||
    bcc !== "" ||
    subject !== initial.current.subject ||
    body !== autoBody.current ||
    files.length > 0;
  const reportDirty = useRef(onDirtyChange);
  reportDirty.current = onDirtyChange;
  useEffect(() => {
    reportDirty.current?.(dirty);
  }, [dirty]);

  const replyToId = mode.kind === "reply" || mode.kind === "replyAll" ? mode.original.id : null;
  const forwardId = mode.kind === "forward" ? mode.original.id : null;
  const fields: SendInput = useMemo(
    () => ({ to, cc, bcc, subject, body, replyToId, forwardId, files }),
    [to, cc, bcc, subject, body, replyToId, forwardId, files],
  );
  const draft = useDraftAutosave(fields, dirty, !disabled);
  useImperativeHandle(ref, () => ({ discard: draft.discard, hasDraft: () => draft.draftId !== null }), [
    draft.discard,
    draft.draftId,
  ]);

  const addFiles = (picked: FileList | null) => {
    const list = picked ? Array.from(picked) : [];
    if (list.length === 0) return;
    const added = list.reduce((sum, file) => sum + file.size, 0);
    if (total + added > MAX_ATTACHMENT_BYTES) {
      setError("附件總計不能超過 15 MB");
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...list]);
  };

  const checkRecipients = (): boolean => {
    if (!to.trim() && !cc.trim() && !bcc.trim()) {
      setError("至少要有一位收件人");
      return false;
    }
    return true;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending || disabled) return;
    if (!checkRecipients()) return;
    setSending(true);
    setError(null);
    // 先等正在存的草稿回來(拿到最新的草稿編號),寄出後由後端刪掉它
    const draftMessageId = await draft.settle();
    try {
      const result = await mailbox.send(fields, { draftMessageId });
      const refused = Array.isArray(result?.refused) ? result.refused : [];
      if (refused.length > 0) {
        toast.push(`信寄出了,但這幾位郵件主機不收:${refused.join("、")}`, "error");
      } else {
        toast.push("已寄出", "success");
      }
      onSent(refused);
    } catch (err) {
      draft.resume();
      const message = mailErrorMessage(err, "沒有寄出去,請稍後再試");
      setError(message);
      toast.push(message, "error");
      onError(err);
    } finally {
      setSending(false);
    }
  };

  const schedule = async () => {
    if (sending || disabled) return;
    if (!checkRecipients()) return;
    const iso = taipeiInputToIso(sendAt);
    const problem = scheduleTimeError(iso);
    if (problem || !iso) {
      setError(problem);
      return;
    }
    setSending(true);
    setError(null);
    const draftMessageId = await draft.settle();
    try {
      const got = await mailbox.schedule(fields, iso, { draftMessageId });
      toast.push(`已排程,會在 ${formatTaipeiDateTime(got?.send_at ?? iso)} 寄出`, "success");
      onScheduled();
    } catch (err) {
      draft.resume();
      const message = mailErrorMessage(err, "沒有排程成功,請稍後再試");
      setError(message);
      toast.push(message, "error");
      onError(err);
    } finally {
      setSending(false);
    }
  };

  const draftNote =
    draft.status === "saving"
      ? "正在儲存草稿…"
      : draft.status === "error"
        ? "草稿沒有存成,內容再改時會再試"
        : draft.status === "saved" && draft.draftId
          ? draft.hasUnsaved
            ? "草稿已儲存(之後的修改還沒存)"
            : files.length > 0
              ? "草稿已儲存到「草稿」資料夾(附件不存進草稿,寄出時才帶上)"
              : "草稿已儲存到「草稿」資料夾"
          : "";

  const title = mode.kind === "new" ? "寫信" : mode.kind === "forward" ? "轉寄" : "回覆";

  return (
    <form
      aria-label="寫信"
      onSubmit={submit}
      className="flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--surface)] p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-base font-semibold text-[var(--text)]">{title}</h3>
        <Button
          size="sm"
          variant="ghost"
          aria-label="關閉寫信"
          data-back=""
          icon={<X aria-hidden="true" size={16} />}
          onClick={onClose}
        />
      </div>
      <Input label="收件人" hint="多位用逗號分開" value={to} onChange={(e) => setTo(e.target.value)} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="副本" value={cc} onChange={(e) => setCc(e.target.value)} />
        <Input label="密件副本" value={bcc} onChange={(e) => setBcc(e.target.value)} />
      </div>
      <Input label="主旨" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <div className="flex flex-col gap-1">
        <label htmlFor={bodyId} className="text-sm font-medium text-[var(--text-2)]">
          內文
        </label>
        <textarea
          id={bodyId}
          rows={12}
          value={body}
          onChange={(e) => {
            touched.current = true;
            setBody(e.target.value);
          }}
          className="rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1.5 text-sm text-[var(--text)]"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={filesId} className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-2)]">
          <Paperclip aria-hidden="true" size={14} />
          附件
        </label>
        <input
          id={filesId}
          type="file"
          multiple
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          className="text-sm text-[var(--text-2)]"
        />
        <span className="text-xs text-[var(--text-3)]">
          總計 15 MB 以內{mode.kind === "forward" ? ";原信的附件會一起轉寄(也算在 15 MB 裡)" : ""}
        </span>
        {files.length > 0 ? (
          <ul className="m-0 flex flex-col gap-1 p-0">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex list-none items-center gap-2 text-sm text-[var(--text)]">
                <span className="min-w-0 truncate">{file.name}</span>
                <span className="text-xs text-[var(--text-3)]">({formatSize(file.size)})</span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`移除附件 ${file.name}`}
                  icon={<X aria-hidden="true" size={14} />}
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}

      {scheduling ? (
        <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] p-2">
          <Input
            label="預定寄出時間(台灣時間)"
            type="datetime-local"
            value={sendAt}
            onChange={(e) => setSendAt(e.target.value)}
          />
          <Button
            variant="primary"
            loading={sending}
            disabled={disabled || !sendAt}
            icon={<CalendarClock aria-hidden="true" size={16} />}
            onClick={() => void schedule()}
          >
            排程寄出
          </Button>
          <span className="text-xs text-[var(--text-3)]">
            系統保管這封信,到時候用你的信箱寄出;寄出前可在「已排程」改時間或取消。
          </span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <span role="status" className="mr-auto text-xs text-[var(--text-3)]">
          {draftNote}
        </span>
        <Button onClick={onClose}>取消</Button>
        <Button
          aria-expanded={scheduling}
          disabled={disabled}
          icon={<CalendarClock aria-hidden="true" size={16} />}
          onClick={() => setScheduling((v) => !v)}
        >
          排程…
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={sending}
          disabled={disabled}
          icon={<Send aria-hidden="true" size={16} />}
        >
          寄出
        </Button>
      </div>
    </form>
  );
});
